/**
 * M2 — Categorías de Materiales (CRUD dedicado)
 * ═══════════════════════════════════════════════════════════════════
 * Página dedicada para gestionar las categorías de materiales utilizadas en:
 *   • Receta por tipo de solución (materiales permitidos al cerrar OT)
 *   • Clasificación de items de inventario
 *
 * Antes vivían dentro del modal de sucursales (según QA 23-07 M2).
 * Ahora tienen su propia sección con búsqueda, ver archivadas, CRUD.
 */
import { useEffect, useState, useRef } from "react";
import Layout from "../components/Layout";
import EmptyState from "../components/EmptyState";
import SlideOverForm, { SlideOverSection } from "../components/SlideOverForm";
import WizardPopover from "../components/WizardPopover";
import api from "../lib/api";
import { toast } from "sonner";
import {
  Plus,
  Search,
  Loader2,
  Layers,
  Edit2,
  Archive,
  RotateCcw,
  Info,
  Tag,
  FileText as FileTextIcon,
} from "lucide-react";

export default function CategoriasMateriales() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [editing, setEditing] = useState(null); // null | {} for new | item for edit
  const [editingFull, setEditingFull] = useState(false);
  const editAnchorRef = useRef(null);

  const openEditor = (item, anchorEl) => {
    editAnchorRef.current = anchorEl || null;
    setEditingFull(false);
    setEditing(item);
  };

  const closeEditor = () => {
    setEditing(null);
    setEditingFull(false);
    editAnchorRef.current = null;
  };

  const fetchItems = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/v1/config/material-categories", {
        params: { include_archived: showArchived },
      });
      setItems(data.items || []);
    } catch (e) {
      toast.error("Error al cargar categorías");
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, [showArchived]);

  const filtered = items.filter((c) => {
    const q = search.toLowerCase().trim();
    if (!q) return true;
    return (
      c.name.toLowerCase().includes(q) ||
      c.code.toLowerCase().includes(q) ||
      (c.description || "").toLowerCase().includes(q)
    );
  });

  const handleSave = async (form) => {
    try {
      if (form.id) {
        await api.patch(`/v1/config/material-categories/${form.id}`, {
          name: form.name,
          description: form.description || null,
        });
        toast.success("Categoría actualizada");
      } else {
        await api.post(`/v1/config/material-categories`, {
          name: form.name,
          description: form.description || null,
          code: form.code || undefined,
        });
        toast.success("Categoría creada");
      }
      setEditing(null);
      fetchItems();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar");
    }
  };

  const handleArchive = async (item) => {
    if (!window.confirm(`¿Archivar la categoría "${item.name}"?`)) return;
    try {
      await api.delete(`/v1/config/material-categories/${item.id}`);
      toast.success("Categoría archivada");
      fetchItems();
    } catch (e) {
      toast.error("Error al archivar");
    }
  };

  const handleRestore = async (item) => {
    try {
      await api.post(`/v1/config/material-categories/${item.id}/restore`);
      toast.success("Categoría restaurada");
      fetchItems();
    } catch (e) {
      toast.error("Error al restaurar");
    }
  };

  return (
    <Layout
      title="Categorías de Materiales"
      subtitle="Catálogo global · usado en recetas por tipo de solución e inventario"
      actions={
        <button
          onClick={(e) => openEditor({}, e.currentTarget)}
          className="flex items-center gap-2 px-4 py-2 rounded-md bg-[#0A3747] text-white text-sm hover:bg-[#135166]"
          data-testid="new-category-btn"
        >
          <Plus size={14} /> Nueva categoría
        </button>
      }
    >
      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-lg p-3 mb-4 flex items-center justify-between flex-wrap gap-3">
        <div className="relative flex-1 max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nombre, código o descripción..."
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-[#E0E621] focus:border-transparent"
            data-testid="category-search"
          />
        </div>
        <label className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer">
          <input
            type="checkbox"
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
            className="accent-[#0A3747]"
            data-testid="category-show-archived"
          />
          Ver archivadas
        </label>
      </div>

      {/* Info banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 flex items-start gap-2">
        <Info size={14} className="text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-[11px] text-blue-800 leading-relaxed">
          <strong>Uso de las categorías:</strong> se referencian desde{" "}
          <em>Tipos de Solución</em> (Receta por tipo → categorías permitidas) y desde{" "}
          <em>Inventario</em> (clasificación de ítems). Archivar una categoría no
          borra los ítems ni los tipos que la usan, pero deja de aparecer en las
          listas de selección.
        </div>
      </div>

      {/* List */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        {loading ? (
          <div className="py-20 flex justify-center">
            <Loader2 className="animate-spin text-gray-400" />
          </div>
        ) : filtered.length === 0 ? (
          <EmptyState
            title="Sin categorías"
            description={search ? "Prueba ajustando la búsqueda." : "Crea la primera categoría."}
            action={
              !search && (
                <button
                  onClick={(e) => openEditor({}, e.currentTarget)}
                  className="px-4 py-2 bg-[#0A3747] text-white rounded text-sm hover:bg-[#135166]"
                >
                  <Plus size={14} className="inline mr-1" /> Nueva categoría
                </button>
              )
            }
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="categories-table">
              <thead className="bg-[#0A3747] text-white text-[10px] uppercase font-mono tracking-wider">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold">Nombre</th>
                  <th className="px-3 py-2 text-left font-semibold">Código</th>
                  <th className="px-3 py-2 text-left font-semibold">Descripción</th>
                  <th className="px-3 py-2 text-center font-semibold">Estado</th>
                  <th className="px-3 py-2 text-right font-semibold">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => (
                  <tr
                    key={c.id}
                    className={`border-t border-gray-100 hover:bg-gray-50 ${!c.active ? "opacity-60" : ""}`}
                    data-testid={`category-row-${c.code}`}
                  >
                    <td className="px-3 py-2 font-medium text-gray-900">
                      <span className="inline-flex items-center gap-2">
                        <Tag size={12} className="text-[#0A3747]" />
                        <span className={!c.active ? "line-through" : ""}>{c.name}</span>
                      </span>
                    </td>
                    <td className="px-3 py-2 font-mono text-[11px] text-gray-600">
                      {c.code}
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-600 max-w-md truncate">
                      {c.description || <span className="italic text-gray-400">—</span>}
                    </td>
                    <td className="px-3 py-2 text-center">
                      {c.active ? (
                        <span className="text-[10px] px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 font-semibold">
                          Activa
                        </span>
                      ) : (
                        <span className="text-[10px] px-2 py-0.5 rounded bg-gray-200 text-gray-700 font-semibold">
                          Archivada
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {c.active ? (
                          <>
                            <button
                              onClick={(e) => openEditor(c, e.currentTarget)}
                              className="p-1.5 rounded hover:bg-blue-50 text-blue-600"
                              title="Editar"
                              data-testid={`category-edit-${c.code}`}
                            >
                              <Edit2 size={13} />
                            </button>
                            <button
                              onClick={() => handleArchive(c)}
                              className="p-1.5 rounded hover:bg-red-50 text-red-600"
                              title="Archivar"
                              data-testid={`category-archive-${c.code}`}
                            >
                              <Archive size={13} />
                            </button>
                          </>
                        ) : (
                          <button
                            onClick={() => handleRestore(c)}
                            className="p-1.5 rounded hover:bg-emerald-50 text-emerald-600"
                            title="Restaurar"
                            data-testid={`category-restore-${c.code}`}
                          >
                            <RotateCcw size={13} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing && editingFull && (
        <CategoryForm
          initial={editing}
          onClose={closeEditor}
          onSave={handleSave}
        />
      )}

      {editing && !editingFull && (
        <CategoryWizard
          initial={editing}
          anchorRef={editAnchorRef}
          onClose={closeEditor}
          onOpenFull={() => setEditingFull(true)}
          onSave={handleSave}
        />
      )}
    </Layout>
  );
}

function CategoryWizard({ initial, anchorRef, onClose, onOpenFull, onSave }) {
  const isEdit = !!initial.id;
  const [form, setForm] = useState({
    id: initial.id,
    name: initial.name || "",
    code: initial.code || "",
    description: initial.description || "",
  });
  const [saving, setSaving] = useState(false);

  const doSave = async () => {
    if (!form.name.trim() || form.name.trim().length < 2) {
      toast.error("El nombre debe tener al menos 2 caracteres");
      return;
    }
    setSaving(true);
    try {
      await onSave(form);
    } finally {
      setSaving(false);
    }
  };

  return (
    <WizardPopover
      open={true}
      onOpenChange={(o) => !o && onClose()}
      anchorRef={anchorRef}
      title={isEdit ? `Editar · ${initial.name}` : "Nueva categoría"}
      subtitle={isEdit ? initial.code : "Se generará código único"}
      icon={Layers}
      saving={saving}
      saveLabel={isEdit ? "Guardar" : "Crear"}
      onSave={doSave}
      onOpenFull={onOpenFull}
      testid="category-wizard"
      width={440}
      steps={[
        {
          key: "identity",
          title: "Identificación",
          icon: Tag,
          content: (
            <div className="space-y-3">
              <div>
                <label>Nombre *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Ej: Fibra óptica multimodo"
                  data-testid="category-name-input"
                  autoFocus
                />
              </div>
              {isEdit && (
                <div>
                  <label>Código</label>
                  <input
                    type="text"
                    value={form.code}
                    disabled
                    data-testid="category-code-input"
                    style={{ opacity: 0.6, cursor: "not-allowed", fontFamily: "'JetBrains Mono', monospace" }}
                  />
                  <p className="text-[10px] mt-1" style={{ color: "var(--text-muted)" }}>
                    El código no se puede modificar tras la creación.
                  </p>
                </div>
              )}
            </div>
          ),
        },
        {
          key: "description",
          title: "Descripción",
          icon: FileTextIcon,
          content: (
            <div>
              <label>Descripción (opcional)</label>
              <textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Describe brevemente qué tipos de material entran en esta categoría..."
                rows={5}
                data-testid="category-desc-input"
                maxLength={250}
                style={{ resize: "none" }}
              />
              <div className="text-[10px] mt-1 text-right" style={{ color: "var(--text-muted)" }}>
                {form.description.length}/250
              </div>
            </div>
          ),
        },
      ]}
    />
  );
}

function CategoryForm({ initial, onClose, onSave }) {
  const isEdit = !!initial.id;
  const [form, setForm] = useState({
    id: initial.id,
    name: initial.name || "",
    code: initial.code || "",
    description: initial.description || "",
  });
  const [saving, setSaving] = useState(false);

  const doSave = async () => {
    if (!form.name.trim() || form.name.trim().length < 2) {
      toast.error("El nombre debe tener al menos 2 caracteres");
      return;
    }
    setSaving(true);
    try {
      await onSave(form);
    } finally {
      setSaving(false);
    }
  };

  return (
    <SlideOverForm
      title={isEdit ? `Editar categoría · ${initial.name}` : "Nueva categoría de material"}
      breadcrumb={`Configuración › Categorías › ${isEdit ? "Editar" : "Nueva"}`}
      subtitle={isEdit ? initial.code : "Se generará un código único automáticamente"}
      onClose={onClose}
      testId="category-form"
      footerActions={
        <>
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900"
            data-testid="category-cancel"
          >
            Cancelar
          </button>
          <button
            onClick={doSave}
            disabled={saving}
            className="ml-auto px-4 py-2 bg-[#0A3747] text-white text-sm rounded hover:bg-[#135166] disabled:opacity-50 flex items-center gap-2"
            data-testid="category-save"
          >
            {saving && <Loader2 size={14} className="animate-spin" />}
            {isEdit ? "Guardar cambios" : "Crear categoría"}
          </button>
        </>
      }
    >
      <SlideOverSection title="Identificación" icon={Tag}>
        <div className="space-y-3">
          <div>
            <label className="block text-[11px] font-mono uppercase tracking-wider text-gray-600 mb-1">
              Nombre <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Ej: Fibra óptica multimodo"
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-[#E0E621] focus:border-transparent"
              data-testid="category-name-input"
              autoFocus
            />
          </div>
          {isEdit && (
            <div>
              <label className="block text-[11px] font-mono uppercase tracking-wider text-gray-600 mb-1">
                Código
              </label>
              <input
                type="text"
                value={form.code}
                disabled
                className="w-full px-3 py-2 border border-gray-200 rounded text-sm bg-gray-50 font-mono text-gray-600"
                data-testid="category-code-input"
              />
              <div className="text-[10px] text-gray-500 mt-1">
                El código no se puede modificar tras la creación.
              </div>
            </div>
          )}
        </div>
      </SlideOverSection>

      <SlideOverSection title="Descripción" icon={FileTextIcon}>
        <textarea
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          placeholder="Describe brevemente qué tipos de material entran en esta categoría..."
          rows={4}
          className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-[#E0E621] focus:border-transparent resize-none"
          data-testid="category-desc-input"
          maxLength={250}
        />
        <div className="text-[10px] text-gray-500 mt-1 text-right">
          {form.description.length}/250
        </div>
      </SlideOverSection>
    </SlideOverForm>
  );
}
